import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, Copy, Loader2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
    type AssistantPromptAction,
    DEFAULT_SHIFT_ACTION_POLICY,
    type ReferenceData,
    type ShiftActionPolicy,
    type ShiftTab,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import {
    type V3PresentationContext,
    V3UserUploadAttachmentCard,
} from '@apps/web/components/phoebe_chat/chat_attachment_card';
import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    USER_MESSAGE_BUBBLE_CLASSES,
    WORKBENCH_USER_MESSAGE_BUBBLE_CLASSES,
} from '@apps/web/components/phoebe_chat/entity_visuals';
import {
    buildSlackMentionMap,
    processSlackUserMentions,
    type SlackUserMentionData,
} from '@apps/web/components/phoebe_chat/slack_mentions';
import { AssistantResponseContent } from '@apps/web/components/phoebe_chat_view/assistant_responses/assistant_response_content';
import { AssistantResponseDestinationLinks } from '@apps/web/components/phoebe_chat_view/assistant_responses/assistant_response_destination_links';
import {
    formatReferenceFallbackDisplayName,
    ReferenceBadge,
    replaceLegacyAutomationEventReferences,
} from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import {
    PhoebeAgentMark,
    SlackSourceIcon,
} from '@apps/web/components/phoebe_chat_view/brand_marks';
import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    type AssistantMessageActionRenderer,
    type MessageArtifactRenderer,
    type MessageEntry,
    type MessageImageAttachment,
    type MessageUploadAttachment,
    type RenderTimelineEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { useLatchedAnimationEligibility } from '@apps/web/components/phoebe_chat_view/use_latched_animation_eligibility';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    getAvatarSolidToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { useTypewriterText } from '@apps/web/hooks/use_typewriter_text';
import {
    ENTITY_REFERENCE_PATTERN,
    resolveEntityReferences,
} from '@apps/web/lib/entity_references';
import { useMode } from '@apps/web/lib/mode_store';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';

function markdownTableCells(line: string): Array<string> {
    const trimmed = line.trim();
    if (!trimmed.includes('|')) {
        return [];
    }
    return trimmed
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());
}

function isMarkdownTableDelimiter(line: string): boolean {
    const cells = markdownTableCells(line);
    return cells.length >= 2 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

function stripMarkdownTables(content: string): string {
    const lines = content.replace(/\r\n/g, '\n').split('\n');
    const keptLines: Array<string> = [];
    let fenceMarker: '`' | '~' | null = null;

    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index] ?? '';
        const trimmed = line.trimStart();
        const nextFenceMarker = trimmed.startsWith('```')
            ? '`'
            : trimmed.startsWith('~~~')
              ? '~'
              : null;
        if (nextFenceMarker !== null) {
            if (fenceMarker === null) {
                fenceMarker = nextFenceMarker;
            } else if (fenceMarker === nextFenceMarker) {
                fenceMarker = null;
            }
            keptLines.push(line);
            continue;
        }

        const nextLine = lines[index + 1];
        if (
            fenceMarker === null &&
            markdownTableCells(line).length >= 2 &&
            nextLine !== undefined &&
            isMarkdownTableDelimiter(nextLine)
        ) {
            index += 2;
            while (
                index < lines.length &&
                markdownTableCells(lines[index] ?? '').length >= 2
            ) {
                index += 1;
            }
            index -= 1;
            continue;
        }

        keptLines.push(line);
    }

    return keptLines
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function UserMessageContent({
    content,
    references,
    slackUserMentions,
}: {
    readonly content: string;
    readonly references?: Array<ReferenceData>;
    readonly slackUserMentions?: Array<SlackUserMentionData>;
}) {
    const refMap = useMemo(
        // Normalize the keys to lowercase so lookups match both the
        // legacy-rewrite path (which lowercases UUIDs in the inserted
        // `[[type:uuid]]` tokens) and persisted references that may carry
        // mixed-case UUIDs.
        () => new Map((references ?? []).map((r) => [r.id.toLowerCase(), r])),
        [references],
    );
    const slackMentionMap = useMemo(
        () => buildSlackMentionMap(slackUserMentions),
        [slackUserMentions],
    );
    const displayContent = useMemo(() => {
        const withReferences = replaceLegacyAutomationEventReferences(
            content,
            references,
        );
        return processSlackUserMentions(withReferences, slackMentionMap);
    }, [content, references, slackMentionMap]);

    const parts = useMemo(() => {
        const result: Array<React.ReactNode> = [];
        let lastIndex = 0;
        const pattern = new RegExp(ENTITY_REFERENCE_PATTERN.source, 'g');
        let match;

        while ((match = pattern.exec(displayContent)) !== null) {
            if (match.index > lastIndex) {
                result.push(displayContent.slice(lastIndex, match.index));
            }
            const refType = (match[1] ?? 'reference').toLowerCase();
            const refId = match[2] ?? '';
            const refData = refMap.get(refId.toLowerCase());
            result.push(
                <ReferenceBadge
                    key={`${refType}:${refId}:${match.index}`}
                    reference={
                        refData ?? {
                            type: refType,
                            id: refId,
                            display_name:
                                formatReferenceFallbackDisplayName(refType),
                        }
                    }
                />,
            );
            lastIndex = pattern.lastIndex;
        }

        if (lastIndex < displayContent.length) {
            result.push(displayContent.slice(lastIndex));
        }

        return result;
    }, [displayContent, refMap]);

    if (parts.length === 1 && typeof parts[0] === 'string') {
        return <React.Fragment>{parts[0]}</React.Fragment>;
    }

    return <React.Fragment>{parts}</React.Fragment>;
}

export function PendingMessagePill({
    content,
    label,
    references,
}: {
    readonly content: string;
    readonly label?: string;
    readonly references?: Array<ReferenceData>;
}) {
    return (
        <div className="bg-muted/60 text-foreground/90 flex items-center gap-2 rounded-xl border border-transparent px-3 py-2 text-sm shadow-sm">
            <Loader2 className="text-muted-foreground h-3.5 w-3.5 shrink-0 animate-spin" />
            {label ? (
                <span className="text-muted-foreground shrink-0 text-[11px] font-medium uppercase">
                    {label}
                </span>
            ) : null}
            <span className="truncate [overflow-wrap:anywhere] break-words whitespace-pre-wrap">
                <UserMessageContent content={content} references={references} />
            </span>
        </div>
    );
}

function UserMessageUploadAttachments({
    uploads,
    context,
}: {
    readonly uploads?: ReadonlyArray<MessageUploadAttachment>;
    readonly context: V3PresentationContext;
}) {
    if (!uploads || uploads.length === 0) {
        return null;
    }
    return (
        <div className="mt-2 flex flex-col gap-1 whitespace-normal">
            {uploads.map((upload) => (
                <V3UserUploadAttachmentCard
                    key={upload.attachmentId}
                    upload={upload}
                    context={context}
                />
            ))}
        </div>
    );
}

function UserMessageImageAttachments({
    images,
}: {
    readonly images?: ReadonlyArray<MessageImageAttachment>;
}) {
    if (!images || images.length === 0) {
        return null;
    }
    const { apiUrl } = getRuntimeConfig();

    return (
        <div className="mt-2 grid grid-cols-2 gap-2 whitespace-normal">
            {images.map((image) => {
                const imageUrl = `${apiUrl}/admin/agent/runs/${image.runId}/artifacts/${image.artifactId}/image`;
                const label = image.filename ?? 'Uploaded image';
                return (
                    <a
                        key={image.artifactId}
                        href={imageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="group/image block overflow-hidden rounded-[7px] border border-black/10 bg-white/40 dark:border-white/10 dark:bg-black/40"
                    >
                        <img
                            src={imageUrl}
                            alt={label}
                            className="aspect-square w-full object-cover transition-opacity group-hover/image:opacity-90"
                            loading="lazy"
                        />
                    </a>
                );
            })}
        </div>
    );
}

function UserMessageAvatar({
    sentBy,
    isSlackSource,
    slackAuthorLabel,
}: {
    readonly sentBy: MessageEntry['sentBy'];
    readonly isSlackSource: boolean;
    readonly slackAuthorLabel: string | null;
}) {
    // Attribute only what the server resolved. `sent_by` is null whenever an
    // event has no enqueuing user, so guessing the viewer here would stamp
    // their identity onto a colleague's message.
    const author = sentBy
        ? { displayName: sentBy.display_name, colorKey: sentBy.user_id }
        : isSlackSource && slackAuthorLabel
          ? { displayName: slackAuthorLabel, colorKey: slackAuthorLabel }
          : null;

    // Keep the inset so the bubble stays aligned when the author is unknown.
    if (!author) {
        return <div className="size-7 shrink-0" />;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span className="inline-flex shrink-0">
                    <UserAvatar
                        className="size-7"
                        displayName={author.displayName}
                        toneClassName={cn(
                            getAvatarSolidToneClass(author.colorKey),
                            'text-xs font-semibold',
                        )}
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent side="left">{author.displayName}</TooltipContent>
        </Tooltip>
    );
}

export function formatChatTypingLabel(
    typingUsers: ReadonlyArray<{ readonly displayName: string }>,
): string {
    const names = typingUsers.map((typingUser) => typingUser.displayName);
    if (names.length === 1) {
        return `${names[0]} is typing…`;
    }
    if (names.length === 2) {
        return `${names[0]} and ${names[1]} are typing…`;
    }
    const otherCount = names.length - 2;
    return `${names[0]}, ${names[1]}, and ${otherCount} ${
        otherCount === 1 ? 'other' : 'others'
    } are typing…`;
}

export function MessageBubble({
    message,
    entries,
    animateContent = false,
    onTypewriterProgress,
    onCaregiverClick,
    onClientClick,
    onAssistantPromptAction,
    shiftSuggestionTabs,
    onRetryAfterRunError,
    isRetryDisabled = false,
    shiftActionPolicy = DEFAULT_SHIFT_ACTION_POLICY,
    presentation = 'default',
    renderMessageArtifacts,
    renderAssistantMessageActions,
    renderMessageFeedback,
    highlightedAgentItemId = null,
}: {
    message: MessageEntry;
    entries?: ReadonlyArray<RenderTimelineEntry>;
    animateContent?: boolean;
    onTypewriterProgress?: () => void;
    onCaregiverClick?: (caregiverId: string) => void;
    onClientClick?: (clientId: string) => void;
    onAssistantPromptAction?: AssistantPromptAction;
    shiftSuggestionTabs?: {
        readonly tabs: Array<ShiftTab>;
        readonly turnSequence: number;
    } | null;
    onRetryAfterRunError?: () => void;
    isRetryDisabled?: boolean;
    shiftActionPolicy?: ShiftActionPolicy;
    presentation?: ToolCardPresentation;
    renderMessageArtifacts?: MessageArtifactRenderer;
    renderAssistantMessageActions?: AssistantMessageActionRenderer;
    renderMessageFeedback?: AssistantMessageActionRenderer;
    highlightedAgentItemId?: string | null;
}) {
    const isUser = message.role === 'user';
    const messageFeedback = renderMessageFeedback?.(message) ?? null;
    const usesWorkbenchPresentation = presentation === 'minimal';
    const [copied, setCopied] = useState(false);
    const { organizationId, timezone } = usePhoebeChatOrganizationScope();
    const mode = useMode() ?? 'live';
    const attachmentContext: V3PresentationContext | null =
        organizationId === null
            ? null
            : {
                  organizationId,
                  organizationTimezone: timezone,
                  mode,
              };
    const shouldAnimateContent = useLatchedAnimationEligibility(
        !isUser && animateContent,
    );
    const visibleContent = message.suppressMarkdownTables
        ? stripMarkdownTables(message.content)
        : message.content;
    const renderedContent = useTypewriterText(visibleContent, {
        animate: shouldAnimateContent,
        animationKey: message.id,
        onRevealFrame: shouldAnimateContent ? onTypewriterProgress : undefined,
    });
    const fallbackArtifactContent =
        renderMessageArtifacts === undefined &&
        message.artifactRefs !== undefined &&
        message.artifactRefs.length > 0 ? (
            <div className="flex flex-wrap gap-2">
                {message.artifactRefs.map((artifactRef) => {
                    const title =
                        artifactRef.title.trim().length > 0
                            ? artifactRef.title
                            : 'Attached artifact';
                    return (
                        <span
                            key={artifactRef.artifact_id}
                            className="bg-muted text-muted-foreground inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
                        >
                            Attached: {title}
                        </span>
                    );
                })}
            </div>
        ) : null;
    const artifactContent =
        renderMessageArtifacts?.(message, entries) ?? fallbackArtifactContent;
    const hasRenderedText = renderedContent.trim().length > 0;

    const formattedTime = new Date(message.createdAt).toLocaleTimeString(
        'en-US',
        {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone,
        },
    );

    function handleCopy() {
        const copyText = resolveEntityReferences(
            visibleContent,
            message.references,
        );
        void navigator.clipboard.writeText(copyText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }

    if (isUser) {
        const slackAuthorLabel =
            message.source === 'slack' ? (message.authorLabel ?? null) : null;
        return (
            <div className="group/msg min-w-0">
                {message.source === 'slack' ? (
                    <div className="text-muted-foreground mb-1 flex items-center justify-end gap-1.5 text-xs">
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span
                                    aria-label={
                                        slackAuthorLabel
                                            ? `${slackAuthorLabel} sent this from Slack`
                                            : 'Sent from Slack'
                                    }
                                    className="inline-flex items-center"
                                >
                                    <SlackSourceIcon className="h-3.5 w-3.5" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                                Sent from Slack
                            </TooltipContent>
                        </Tooltip>
                    </div>
                ) : null}
                <div className="flex items-center justify-end gap-2">
                    <div
                        className={cn(
                            'whitespace-pre-wrap',
                            usesWorkbenchPresentation
                                ? WORKBENCH_USER_MESSAGE_BUBBLE_CLASSES
                                : USER_MESSAGE_BUBBLE_CLASSES,
                        )}
                    >
                        <UserMessageContent
                            content={message.content}
                            references={message.references}
                            slackUserMentions={message.slackUserMentions}
                        />
                        <UserMessageImageAttachments
                            images={message.imageAttachments}
                        />
                        {attachmentContext !== null ? (
                            <UserMessageUploadAttachments
                                uploads={message.uploadAttachments}
                                context={attachmentContext}
                            />
                        ) : null}
                    </div>
                    <UserMessageAvatar
                        sentBy={message.sentBy}
                        isSlackSource={message.source === 'slack'}
                        slackAuthorLabel={slackAuthorLabel}
                    />
                </div>
                {message.destinationLinks &&
                message.destinationLinks.length > 0 ? (
                    <div className="flex justify-end pr-9">
                        <AssistantResponseDestinationLinks
                            destinationLinks={message.destinationLinks}
                        />
                    </div>
                ) : null}
                <div className="text-muted-foreground mt-1 flex items-center justify-end gap-1.5 pr-9 text-xs opacity-0 transition-opacity group-hover/msg:opacity-100">
                    <span>{formattedTime}</span>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="hover:text-foreground cursor-pointer transition-colors"
                    >
                        {copied ? (
                            <Check className="h-3 w-3" />
                        ) : (
                            <Copy className="h-3 w-3" />
                        )}
                    </button>
                </div>
            </div>
        );
    }

    if (message.isRunError) {
        return (
            <div className="text-destructive text-sm">
                <span>Run failed - </span>
                {onRetryAfterRunError ? (
                    <button
                        type="button"
                        disabled={isRetryDisabled}
                        onClick={onRetryAfterRunError}
                        className="cursor-pointer underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        click here to retry
                    </button>
                ) : null}
            </div>
        );
    }

    if (!hasRenderedText && !artifactContent) {
        return null;
    }

    return (
        <div
            className={cn(
                'group/msg w-full max-w-full min-w-0',
                usesWorkbenchPresentation && 'relative ml-7',
            )}
        >
            {usesWorkbenchPresentation ? (
                <PhoebeAgentMark className="absolute top-2 -left-7 size-5" />
            ) : null}
            <TooltipPrimitive.Provider delayDuration={200}>
                {hasRenderedText ? (
                    <div
                        data-testid="assistant-message-content"
                        data-agent-item-id={message.agentItemId}
                        data-feedback-highlighted={
                            message.agentItemId === highlightedAgentItemId
                                ? 'true'
                                : undefined
                        }
                        className={cn(
                            'text-foreground w-full max-w-full min-w-0 overflow-x-hidden rounded-md [overflow-wrap:anywhere] break-words transition-[background-color,box-shadow] duration-500 motion-reduce:transition-none',
                            usesWorkbenchPresentation
                                ? 'rounded-[7px] border border-[#DBE3E3] bg-[#FFFFFF]/95 px-3.5 py-3 text-[13px] leading-6 text-[#002429] shadow-[0_1px_0_rgba(0,36,41,0.04)]'
                                : 'text-sm',
                            message.agentItemId === highlightedAgentItemId &&
                                'bg-amber-50 ring-2 ring-amber-300 ring-offset-2 dark:bg-amber-950/50 dark:ring-amber-800',
                            message.invalidatedByDeniedApproval &&
                                'text-muted-foreground line-through opacity-60',
                        )}
                    >
                        <AssistantResponseContent
                            content={renderedContent}
                            destinationLinks={message.destinationLinks}
                            references={message.references}
                            slackUserMentions={message.slackUserMentions}
                            openLinksInNewTab={usesWorkbenchPresentation}
                            onCaregiverClick={onCaregiverClick}
                            onAssistantPromptAction={onAssistantPromptAction}
                            shiftSuggestionTabs={shiftSuggestionTabs}
                            shiftActionPolicy={shiftActionPolicy}
                        />
                    </div>
                ) : null}
                {artifactContent ? (
                    <div className={cn(hasRenderedText && 'mt-2')}>
                        {artifactContent}
                    </div>
                ) : null}
            </TooltipPrimitive.Provider>
            <div
                className={cn(
                    'text-muted-foreground mt-1 flex items-center gap-1.5 text-xs',
                    usesWorkbenchPresentation && 'ml-1 text-[11px]',
                )}
            >
                {/* A rateable message keeps its whole row on screen, so the
                    thumbs are discoverable and a recorded rating is visible
                    without hunting for it. Surfaces without feedback (the
                    admin agent) keep the original hover reveal. */}
                <span
                    className={cn(
                        'flex items-center gap-1.5 transition-opacity',
                        messageFeedback === null &&
                            'opacity-0 group-hover/msg:opacity-100',
                    )}
                >
                    <span>{formattedTime}</span>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="hover:text-foreground cursor-pointer transition-colors"
                    >
                        {copied ? (
                            <Check className="h-3 w-3" />
                        ) : (
                            <Copy className="h-3 w-3" />
                        )}
                    </button>
                    {renderAssistantMessageActions?.(message)}
                </span>
                {messageFeedback}
            </div>
        </div>
    );
}
